import { useState } from "react";
import type { ReferralSubmission } from "@sunrun/experience-ui-components";
import { createReferral } from "services/referral";
import { logger } from "providers/logger";

type UseReferralParams = {
  contactId: string | undefined;
  authToken: string | undefined;
};

type UseReferralResult = {
  isSubmitting: boolean;
  error: string | null;
  success: boolean;
  submitReferral: (referral: ReferralSubmission) => Promise<void>;
  clearError: () => void;
  clearSuccess: () => void;
};

const getReferralErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
};

const useReferral = ({
  contactId,
  authToken,
}: UseReferralParams): UseReferralResult => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const submitReferral = async (
    referral: ReferralSubmission
  ): Promise<void> => {
    if (!authToken) {
      const message = "Authentication token not available.";
      setError(message);
      throw new Error(message);
    }

    if (!contactId) {
      const message = "Unable to submit referral. Customer contact not found.";
      setError(message);
      throw new Error(message);
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      await createReferral(authToken, { ...referral, contactId });
      setSuccess(true);
    } catch (e: unknown) {
      const errorMessage = getReferralErrorMessage(
        e,
        "Failed to submit referral."
      );
      setError(errorMessage);
      logger.error("Failed to submit referral", {
        error: e instanceof Error ? e : undefined,
      });
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearError = () => setError(null);
  const clearSuccess = () => setSuccess(false);

  return {
    isSubmitting,
    error,
    success,
    submitReferral,
    clearError,
    clearSuccess,
  };
};

export { useReferral };
